import { forwardRef, useEffect, useRef, useState } from 'react';
import { Group, Rect, Line, Path, Text, Circle as KonvaCircle } from 'react-konva';
import Konva from 'konva';
import { Node } from '../../../../core/models/scene';
import { BPMN_SHAPES } from '../../../process/shapes/bpmn/BpmnShapes.defs';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { useThemeAwareColor } from '../../../../react-app/theme/ThemeProvider';

type TextAlign = 'left' | 'center' | 'right' | 'justify';

const getBpmnAiDef = (variant: string) =>
  (BPMN_SHAPES as Record<string, { width: number; height: number; paths: string[]; fillPaths?: string[] }>)[variant];

interface BpmnShapeNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  onStartTextEdit?: (textNode: any) => void;
  editing?: boolean;
  canWrite?: boolean;
  liveSize?: { w: number; h: number };
}

const resolveBpmnVariant = (node: Node): string => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const candidates = [attrs.variant, attrs.shapeType, (node as any).name, node.type];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const normalized = candidate.toLowerCase().replace(/\s+/g, '-');
      if (normalized.startsWith('bpmn-')) return normalized;
      return normalized;
    }
  }
  return 'bpmn-event';
};

export const BpmnShapeNodeBase = forwardRef<Konva.Group, BpmnShapeNodeProps>(({
  node,
  selected: _selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editing = false,
  canWrite = true,
  liveSize,
}, ref) => {
  const [liveDims, setLiveDims] = useState<{ w: number; h: number } | null>(null);
  const baseWidth = (node.width ?? node.w ?? 140) as number;
  const baseHeight = (node.height ?? node.h ?? 80) as number;
  const width = liveSize?.w ?? baseWidth;
  const height = liveSize?.h ?? baseHeight;
  const effectiveW = liveDims?.w ?? width;
  const effectiveH = liveDims?.h ?? height;
  const variant = resolveBpmnVariant(node);
  const isBpmnAnnotation = variant === 'bpmn-annotation';
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const nodeAny = node as any;
  const propsDirect = ((nodeAny?.props as Record<string, unknown>) ?? {});
  const propsAttrs = ((nodeAny?.props?.attrs as Record<string, unknown>) ?? {});

  const groupRef = useRef<Konva.Group | null>(null);
  const bpmnLabelGroupRef = useRef<Konva.Group | null>(null);
  const aiShapeGroupRef = useRef<Konva.Group | null>(null);
  const shapeRef = useRef<Konva.Shape | Konva.Line | Konva.Path | null>(null);
  const textRef = useRef<Konva.Text | null>(null);
  const bpmnTextRef = useRef<Konva.Text | null>(null);

  const getThemeColor = useThemeAwareColor();

  const fillColor = (node.fillColor as string) || (attrs.fillColor as string) || '#F8FAFC';
  const strokeColor = (node.strokeColor as string) || (attrs.strokeColor as string) || '#1E293B';
  const themeFillColor = getThemeColor(fillColor, { type: 'background' });
  const themeStrokeColor = getThemeColor(strokeColor, { type: 'foreground' });
  const strokeWidth = typeof attrs.strokeWidth === 'number' ? (attrs.strokeWidth as number) : typeof node.strokeWidth === 'number' ? node.strokeWidth : 2;
  const stageScale = useDiagramStore((s) => (s.stage?.scale ?? 1));

  const textValue = typeof node.text === 'string' && node.text.trim().length > 0 ? node.text : (typeof attrs.label === 'string' && attrs.label.trim().length > 0) ? attrs.label : '';
  const textColor = (attrs.textColor as string) || '#0F172A';
  const themeTextColor = getThemeColor(textColor, { type: 'foreground' });
  const fontSize = (attrs.fontSize as number) || (node.fontSize as number) || 12;
  const fontFamily = (attrs.fontFamily as string) || (node.fontFamily as string) || 'Inter, sans-serif';
  const hAlign = (attrs.textAlign as TextAlign) || 'center';
  const fontStyle = (() => {
    const fw = (attrs.fontWeight || '').toString().toLowerCase();
    const fs = (attrs.fontStyle || '').toString().toLowerCase();
    const isBold = fw === 'bold' || fw === '700';
    const isItalic = fs === 'italic';
    if (isBold && isItalic) return 'bold italic';
    if (isBold) return 'bold';
    if (isItalic) return 'italic';
    return 'normal';
  })();
  const textDecoration = (attrs.textDecoration as string) || 'none';
  const hasText = textValue && textValue.trim().length > 0;
  const dash = Array.isArray(attrs.dash) ? attrs.dash : [];
  const dashEnabled = !!attrs.dashEnabled && dash.length > 0;
  const snapToGrid = 12;

  useEffect(() => {
    if (variant !== 'bpmn-activity') return;
    try { groupRef.current?.getLayer()?.batchDraw(); } catch {}
  }, [variant, attrs.activityType, attrs.taskType, attrs.activityMarker, attrs.loopType, propsAttrs.activityType, propsAttrs.taskType, propsAttrs.activityMarker, propsAttrs.loopType, propsDirect.activityType, propsDirect.taskType, propsDirect.activityMarker, propsDirect.loopType, nodeAny.activityType, nodeAny.taskType, nodeAny.activityMarker, nodeAny.loopType]);

  useEffect(() => {
    if (variant !== 'bpmn-event') return;
    try { groupRef.current?.getLayer()?.batchDraw(); } catch {}
  }, [variant, attrs.eventType, attrs.interrupting, attrs.eventMode, attrs.trigger, propsAttrs.eventType, propsAttrs.interrupting, propsAttrs.eventMode, propsAttrs.trigger, nodeAny.eventType, nodeAny.interrupting, nodeAny.eventMode, nodeAny.trigger]);

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    if (bpmnLabelGroupRef.current) {
      const pos = e.target.position();
      bpmnLabelGroupRef.current.position({ x: pos.x, y: pos.y });
      try { bpmnLabelGroupRef.current.getLayer()?.batchDraw(); } catch {}
    }
    if (!handler) return;
    handler({ x: e.target.position().x, y: e.target.position().y });
  };

  const fireDragSnapped = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    const pos = e.target.position();
    const gx = Math.round(pos.x / snapToGrid) * snapToGrid;
    const gy = Math.round(pos.y / snapToGrid) * snapToGrid;
    try {
      e.target.position({ x: gx, y: gy });
      if (bpmnLabelGroupRef.current) {
        bpmnLabelGroupRef.current.position({ x: gx, y: gy });
        bpmnLabelGroupRef.current.getLayer()?.batchDraw();
      }
    } catch {}
    if (handler) handler({ x: gx, y: gy });
  };

  const applyDimensions = (nextWidth: number, nextHeight: number) => {
    const group = groupRef.current;
    const shape = shapeRef.current as any;
    if (group) { group.width(nextWidth); group.height(nextHeight); }
    if (variant === 'bpmn-annotation') {
      const armLen = Math.max(10, Math.min(18, nextWidth * 0.18));
      if (shape && typeof (shape as any).points === 'function') {
        try { (shape as Konva.Line).points([armLen, 0, 0, 0, 0, nextHeight, armLen, nextHeight]); } catch {}
      }
      if (textRef.current) {
        const padding = 12;
        const gap = 8;
        const x = Math.min(nextWidth, armLen + gap);
        const w = Math.max(0, nextWidth - x - padding);
        const y = padding;
        const h = Math.max(0, nextHeight - padding * 2);
        try {
          textRef.current.x(x);
          textRef.current.y(y);
          textRef.current.width(w);
          textRef.current.height(h);
        } catch {}
      }
      return;
    }
    const aiDef = getBpmnAiDef(variant);
    if (shape && aiDef) {
      const size = variant === 'bpmn-event' ? Math.min(nextWidth, nextHeight) : null;
      const sx = aiDef.width > 0 ? (size ?? nextWidth) / aiDef.width : 1;
      const sy = aiDef.height > 0 ? (size ?? nextHeight) / aiDef.height : 1;
      const aiGroup = aiShapeGroupRef.current as any;
      if (aiGroup) {
        try {
          aiGroup.scaleX(sx);
          aiGroup.scaleY(sy);
          if (variant === 'bpmn-data-store') {
            aiGroup.x(-12.5 * sx);
            aiGroup.y(-6.6667 * sy);
          } else if (variant === 'bpmn-event' && size) {
            const pathCenterOffset = size / 2 - 24.8 * (size / aiDef.width);
            aiGroup.x((nextWidth - size) / 2 + pathCenterOffset);
            aiGroup.y((nextHeight - size) / 2 + pathCenterOffset);
          } else {
            aiGroup.x(0);
            aiGroup.y(0);
          }
        } catch {}
      } else {
        try { shape.scaleX(sx); shape.scaleY(sy); } catch {}
      }
    }
  };

  useEffect(() => { applyDimensions(width, height); }, [width, height, variant]);

  useEffect(() => {
    if (!liveDims) return;
    const wMatches = Math.abs((liveSize?.w ?? width) - liveDims.w) < 0.5;
    const hMatches = Math.abs((liveSize?.h ?? height) - liveDims.h) < 0.5;
    if (wMatches && hMatches) setLiveDims(null);
  }, [liveDims, width, height, liveSize?.w, liveSize?.h]);

  const handleTransformEnd = (e: any) => {
    // Let the canvas-level transform handler capture geometry and commit it;
    // don’t reset dimensions here or the node can snap back before the commit runs.
    onTransformEnd?.(e);
  };

  const handleTransform = (e: any) => {
    const target = e.target as any;
    const scaleX = target.scaleX?.() ?? 1;
    const scaleY = target.scaleY?.() ?? 1;
    const curW = (groupRef.current?.width?.() as number) || width;
    const curH = (groupRef.current?.height?.() as number) || height;
    const nextW = Math.max(20, curW * scaleX);
    const nextH = Math.max(20, curH * scaleY);
    setLiveDims({ w: nextW, h: nextH });
    applyDimensions(nextW, nextH);
    try { target.scaleX(1); target.scaleY(1); } catch {}
    onTransform?.(e);
  };

  const renderVariant = () => {
    const isBpmnEvent = variant === 'bpmn-event';
    const isBpmnActivity = variant === 'bpmn-activity';
    const shared = {
      fill: themeFillColor,
      stroke: themeStrokeColor,
      strokeWidth,
      strokeEnabled: (isBpmnEvent || isBpmnActivity) ? false : strokeWidth > 0,
      perfectDrawEnabled: false,
      dash: dashEnabled ? dash : undefined,
      dashEnabled,
    };

    if (variant === 'bpmn-annotation') {
      const armLen = Math.max(10, Math.min(18, effectiveW * 0.18));
      const hitFill = 'rgba(0,0,0,0)';
      const zoomScaledStrokeWidth = strokeWidth * stageScale;
      return (
        <>
          <Rect x={0} y={0} width={effectiveW} height={effectiveH} fill={hitFill} strokeEnabled={false} perfectDrawEnabled={false} />
          <Line
            ref={shapeRef as any}
            x={0}
            y={0}
            points={[armLen, 0, 0, 0, 0, effectiveH, armLen, effectiveH]}
            stroke={themeStrokeColor}
            strokeWidth={zoomScaledStrokeWidth}
            lineCap="round"
            lineJoin="round"
            dash={dashEnabled ? dash : undefined}
            dashEnabled={dashEnabled}
            strokeScaleEnabled={false}
            perfectDrawEnabled={false}
          />
        </>
      );
    }

    const renderBpmnEventMarker = () => {
      if (variant !== 'bpmn-event') return null;
      const eventType = String(attrs.eventType ?? propsAttrs.eventType ?? nodeAny.eventType ?? 'start').toLowerCase();
      const interrupting = Boolean((attrs.interrupting ?? propsAttrs.interrupting ?? nodeAny.interrupting ?? true));
      const eventMode = String(attrs.eventMode ?? propsAttrs.eventMode ?? nodeAny.eventMode ?? (eventType === 'end' ? 'throwing' : 'catching')).toLowerCase();
      const trigger = String(attrs.trigger ?? propsAttrs.trigger ?? nodeAny.trigger ?? 'none').toLowerCase();
      const size = Math.min(effectiveW, effectiveH);
      const cx = effectiveW / 2;
      const cy = effectiveH / 2;
      const radius = Math.max(10, size * 0.48);
      const markerStrokeWidth = Math.max(1.2, strokeWidth * 0.9);
      const isDashed = !interrupting;
      const isDouble = eventType === 'intermediate';
      const isEnd = eventType === 'end';
      const isThrowing = eventMode === 'throwing';
      const outerStrokeWidth = isEnd ? 3.2 : isThrowing ? 2.4 : 1.8;
      const innerStrokeWidth = isThrowing ? 2.1 : 1.5;
      const markerProps = { stroke: themeStrokeColor, listening: false } as const;
      const renderTrigger = () => {
        const iconSize = Math.max(6, size * 0.23);
        const left = cx - iconSize;
        const right = cx + iconSize;
        const top = cy - iconSize * 0.72;
        const bottom = cy + iconSize * 0.72;
        const triggerStroke = themeStrokeColor;
        const triggerProps = { ...markerProps, stroke: triggerStroke, strokeWidth: Math.max(1.2, markerStrokeWidth) } as const;

        switch (trigger) {
          case 'message': {
            const isFilled = isThrowing || isEnd;
            const flapStroke = isFilled ? themeFillColor : triggerStroke;
            const vLeftX = left + iconSize * 0.1;
            const vRightX = right - iconSize * 0.1;
            const vTopY = top + iconSize * 0.18;
            const vCenterY = cy + iconSize * 0.12;
            return (
              <>
                <Rect x={left} y={top} width={iconSize * 2} height={iconSize * 1.45} cornerRadius={iconSize * 0.25} stroke={triggerStroke} strokeWidth={triggerProps.strokeWidth} fill={isFilled ? triggerStroke : undefined} fillEnabled={isFilled} listening={false} />
                <Line {...triggerProps} stroke={flapStroke} strokeWidth={isFilled ? Math.max(1.5, triggerProps.strokeWidth) : triggerProps.strokeWidth} points={[vLeftX, vTopY, cx, vCenterY, vRightX, vTopY]} lineCap="round" lineJoin="round" />
              </>
            );
          }
          case 'timer': {
            const r = iconSize * 1.02;
            return (
              <>
                <KonvaCircle {...triggerProps} x={cx} y={cy} radius={r} fillEnabled={false} />
                <Line {...triggerProps} points={[cx, cy, cx, cy - r * 0.46]} />
                <Line {...triggerProps} points={[cx, cy, cx + r * 0.36, cy]} />
              </>
            );
          }
          case 'escalation':
            return <Line {...triggerProps} points={[cx, cy - iconSize * 0.9, cx + iconSize * 0.78, cy + iconSize * 0.82, cx, cy + iconSize * 0.24, cx - iconSize * 0.78, cy + iconSize * 0.82]} closed fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} lineJoin="miter" />;
          case 'conditional':
            return (
              <>
                <Rect x={left + iconSize * 0.1} y={top - iconSize * 0.1} width={iconSize * 1.8} height={iconSize * 1.8} stroke={triggerStroke} strokeWidth={triggerProps.strokeWidth} fillEnabled={false} listening={false} />
                <Line {...triggerProps} points={[left + iconSize * 0.36, top + iconSize * 0.42, right - iconSize * 0.36, top + iconSize * 0.42]} />
                <Line {...triggerProps} points={[left + iconSize * 0.36, cy, right - iconSize * 0.36, cy]} />
                <Line {...triggerProps} points={[left + iconSize * 0.36, bottom - iconSize * 0.42, right - iconSize * 0.36, bottom - iconSize * 0.42]} />
              </>
            );
          case 'link': {
            const shaftHalf = iconSize * 0.24;
            const headBaseX = right - iconSize * 0.52;
            const headTipX = right;
            const headHalf = iconSize * 0.72;
            const linkPath = [`M ${left} ${cy - shaftHalf}`, `L ${headBaseX} ${cy - shaftHalf}`, `L ${headBaseX} ${cy - headHalf}`, `L ${headTipX} ${cy}`, `L ${headBaseX} ${cy + headHalf}`, `L ${headBaseX} ${cy + shaftHalf}`, `L ${left} ${cy + shaftHalf}`, 'Z'].join(' ');
            return <Path {...triggerProps} data={linkPath} fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} lineJoin="round" lineCap="round" />;
          }
          case 'error': {
            const zapSize = iconSize * 1.12;
            const zapPath = [`M ${cx + zapSize * 0.18} ${cy - zapSize * 0.82}`, `L ${cx - zapSize * 0.62} ${cy + zapSize * 0.04}`, `L ${cx - zapSize * 0.10} ${cy + zapSize * 0.04}`, `L ${cx - zapSize * 0.22} ${cy + zapSize * 0.82}`, `L ${cx + zapSize * 0.62} ${cy - zapSize * 0.04}`, `L ${cx + zapSize * 0.10} ${cy - zapSize * 0.04}`, `L ${cx + zapSize * 0.18} ${cy - zapSize * 0.82}`].join(' ');
            return <Path {...triggerProps} data={zapPath} fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} lineJoin="round" lineCap="round" />;
          }
          case 'cancel': {
            const scale = (iconSize * 0.82) / 4.879;
            const sx = (v: number) => cx + (v - 12) * scale;
            const sy = (v: number) => cy + (v - 12) * scale;
            const cancelPath = [`M ${sx(14.758)} ${sy(7.121)}`, `L ${sx(16.879)} ${sy(9.242)}`, `L ${sx(14.121)} ${sy(12)}`, `L ${sx(16.879)} ${sy(14.758)}`, `L ${sx(14.758)} ${sy(16.879)}`, `L ${sx(12)} ${sy(14.121)}`, `L ${sx(9.242)} ${sy(16.879)}`, `L ${sx(7.121)} ${sy(14.758)}`, `L ${sx(9.879)} ${sy(12)}`, `L ${sx(7.121)} ${sy(9.242)}`, `L ${sx(9.242)} ${sy(7.121)}`, `L ${sx(12)} ${sy(9.879)}`, 'Z'].join(' ');
            return <Path {...triggerProps} data={cancelPath} fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} lineJoin="round" lineCap="round" />;
          }
          case 'compensation':
            return (
              <>
                <Line {...triggerProps} points={[cx - iconSize * 0.08, cy - iconSize * 0.62, cx - iconSize * 0.72, cy, cx - iconSize * 0.08, cy + iconSize * 0.62]} closed fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} stroke={triggerStroke} />
                <Line {...triggerProps} points={[cx + iconSize * 0.66, cy - iconSize * 0.62, cx + iconSize * 0.02, cy, cx + iconSize * 0.66, cy + iconSize * 0.62]} closed fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} stroke={triggerStroke} />
              </>
            );
          case 'signal':
            return <Line {...triggerProps} points={[cx, top, right, bottom, left, bottom]} closed fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} />;
          case 'multiple': {
            const topY = cy - iconSize * 0.78;
            const shoulderY = cy - iconSize * 0.22;
            const bottomY = cy + iconSize * 0.68;
            return <Line {...triggerProps} points={[cx, topY, cx + iconSize * 0.86, shoulderY, cx + iconSize * 0.5, bottomY, cx - iconSize * 0.5, bottomY, cx - iconSize * 0.86, shoulderY]} closed fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} lineJoin="round" />;
          }
          case 'parallel-multiple': {
            const plusArm = iconSize * 0.82;
            const plusHalfThickness = iconSize * 0.28;
            const plusPath = [`M ${cx - plusHalfThickness} ${cy - plusArm}`, `L ${cx + plusHalfThickness} ${cy - plusArm}`, `L ${cx + plusHalfThickness} ${cy - plusHalfThickness}`, `L ${cx + plusArm} ${cy - plusHalfThickness}`, `L ${cx + plusArm} ${cy + plusHalfThickness}`, `L ${cx + plusHalfThickness} ${cy + plusHalfThickness}`, `L ${cx + plusHalfThickness} ${cy + plusArm}`, `L ${cx - plusHalfThickness} ${cy + plusArm}`, `L ${cx - plusHalfThickness} ${cy + plusHalfThickness}`, `L ${cx - plusArm} ${cy + plusHalfThickness}`, `L ${cx - plusArm} ${cy - plusHalfThickness}`, `L ${cx - plusHalfThickness} ${cy - plusHalfThickness}`, 'Z'].join(' ');
            return <Path {...triggerProps} data={plusPath} fill={isThrowing || isEnd ? triggerStroke : undefined} fillEnabled={isThrowing || isEnd} closed lineJoin="round" />;
          }
          case 'terminate':
            return <KonvaCircle {...triggerProps} x={cx} y={cy} radius={iconSize * 1.25} fill={triggerStroke} fillEnabled />;
          case 'none':
          default:
            return null;
        }
      };

      return (
        <>
          <KonvaCircle {...markerProps} x={cx} y={cy} radius={radius} strokeWidth={outerStrokeWidth} dash={(isDashed || (isDouble && isThrowing)) ? [4, 3] : undefined} fill={themeFillColor} fillEnabled />
          {isDouble && <KonvaCircle {...markerProps} x={cx} y={cy} radius={radius - 4.8} strokeWidth={innerStrokeWidth} dash={(isDashed || isThrowing) ? [4, 3] : undefined} fillEnabled={false} />}
          {renderTrigger()}
        </>
      );
    };

    const renderBpmnGatewayMarker = () => {
      if (variant !== 'bpmn-gateway') return null;
      const gatewayType = String(attrs.gatewayType ?? 'none').toLowerCase();
      const cx = effectiveW / 2;
      const cy = effectiveH / 2;
      const size = Math.max(8, Math.min(effectiveW, effectiveH) * 0.18);
      const markerStrokeWidth = Math.max(1.4, strokeWidth * 0.9);
      const markerProps = { stroke: themeStrokeColor, strokeWidth: markerStrokeWidth, listening: false } as const;

      switch (gatewayType) {
        case 'exclusive':
          return <><Line {...markerProps} points={[cx - size, cy - size, cx + size, cy + size]} /><Line {...markerProps} points={[cx + size, cy - size, cx - size, cy + size]} /></>;
        case 'parallel':
          return <><Line {...markerProps} points={[cx - size, cy, cx + size, cy]} /><Line {...markerProps} points={[cx, cy - size, cx, cy + size]} /></>;
        case 'inclusive':
          return <KonvaCircle {...markerProps} x={cx} y={cy} radius={size} fillEnabled={false} />;
        case 'event-based': {
          const outerCircle = size * 1.18;
          const innerCircle = size * 0.88;
          const pentagonSize = size * 0.56;
          return (
            <>
              <KonvaCircle {...markerProps} x={cx} y={cy} radius={outerCircle} fillEnabled={false} />
              <KonvaCircle {...markerProps} x={cx} y={cy} radius={innerCircle} fillEnabled={false} />
              <Line {...markerProps} points={[cx, cy - pentagonSize, cx + pentagonSize * 0.95, cy - pentagonSize * 0.3, cx + pentagonSize * 0.58, cy + pentagonSize * 0.8, cx - pentagonSize * 0.58, cy + pentagonSize * 0.8, cx - pentagonSize * 0.95, cy - pentagonSize * 0.3]} closed />
            </>
          );
        }
        case 'exclusive-event':
          return (
            <>
              <KonvaCircle {...markerProps} x={cx} y={cy} radius={size} fillEnabled={false} />
              <Line {...markerProps} points={[cx, cy - size * 0.62, cx + size * 0.59, cy - size * 0.2, cx + size * 0.36, cy + size * 0.5, cx - size * 0.36, cy + size * 0.5, cx - size * 0.59, cy - size * 0.2]} closed />
            </>
          );
        case 'complex':
          return <><Line {...markerProps} points={[cx - size, cy, cx + size, cy]} /><Line {...markerProps} points={[cx, cy - size, cx, cy + size]} /><Line {...markerProps} points={[cx - size * 0.72, cy - size * 0.72, cx + size * 0.72, cy + size * 0.72]} /><Line {...markerProps} points={[cx + size * 0.72, cy - size * 0.72, cx - size * 0.72, cy + size * 0.72]} /></>;
        case 'parallel-event': {
          const plusArm = size * 0.62;
          const plusHalfThickness = size * 0.22;
          const plusPath = [`M ${cx - plusHalfThickness} ${cy - plusArm}`, `L ${cx + plusHalfThickness} ${cy - plusArm}`, `L ${cx + plusHalfThickness} ${cy - plusHalfThickness}`, `L ${cx + plusArm} ${cy - plusHalfThickness}`, `L ${cx + plusArm} ${cy + plusHalfThickness}`, `L ${cx + plusHalfThickness} ${cy + plusHalfThickness}`, `L ${cx + plusHalfThickness} ${cy + plusArm}`, `L ${cx - plusHalfThickness} ${cy + plusArm}`, `L ${cx - plusHalfThickness} ${cy + plusHalfThickness}`, `L ${cx - plusArm} ${cy + plusHalfThickness}`, `L ${cx - plusArm} ${cy - plusHalfThickness}`, `L ${cx - plusHalfThickness} ${cy - plusHalfThickness}`, 'Z'].join(' ');
          return <><KonvaCircle {...markerProps} x={cx} y={cy} radius={size} stroke={themeStrokeColor} fill={themeFillColor} fillEnabled /><Path {...markerProps} stroke={themeStrokeColor} data={plusPath} fill={themeFillColor} fillEnabled closed /></>;
        }
        case 'none':
        default:
          return null;
      }
    };

    const renderBpmnActivityMarkers = () => {
      if (variant !== 'bpmn-activity') return null;
      const activityType = ((attrs.activityType ?? propsAttrs.activityType ?? propsDirect.activityType ?? nodeAny.activityType) as string || 'task').toLowerCase();
      const taskType = ((attrs.taskType ?? propsAttrs.taskType ?? propsDirect.taskType ?? nodeAny.taskType) as string || 'none').toLowerCase();
      const rawMarker = ((attrs.activityMarker ?? propsAttrs.activityMarker ?? propsDirect.activityMarker ?? nodeAny.activityMarker ?? attrs.loopType ?? propsAttrs.loopType ?? propsDirect.loopType ?? nodeAny.loopType) as string || 'none').toLowerCase();
      const markerType = rawMarker === 'standard' ? 'loop' : rawMarker;
      const arr = (attrs.activityMarkers ?? propsAttrs.activityMarkers ?? propsDirect.activityMarkers ?? nodeAny.activityMarkers) as string[] | undefined;
      const markerTypes: string[] = (Array.isArray(arr) && arr.length >= 1)
        ? arr.slice(0, 3).map((m) => (String(m).toLowerCase() === 'standard' ? 'loop' : String(m).toLowerCase())).filter((m) => m && m !== 'none')
        : (markerType && markerType !== 'none' ? [markerType] : []);

      const markerStrokeWidth = Math.max(1.2, strokeWidth * 0.72);
      const baseProps = { stroke: themeStrokeColor, strokeWidth: markerStrokeWidth, listening: false } as const;
      const markerSize = Math.max(6, Math.min(effectiveW, effectiveH) * 0.095);
      const markerVisualSize = markerSize * 0.78;
      const bottomPadding = Math.max(6, markerSize * 0.35);
      const bottomY = effectiveH - markerSize - bottomPadding;
      const topLeftX = Math.max(8, effectiveW * 0.06);
      const topLeftY = Math.max(8, effectiveH * 0.08);
      const taskSize = Math.max(8, Math.min(effectiveW, effectiveH) * 0.13);

      const renderActivityStyle = () => {
        const radius = Math.min(effectiveW, effectiveH) * 0.06;
        const styleStrokeWidth = Math.max(1.4, strokeWidth * 0.9);
        const styleProps = { stroke: themeStrokeColor, strokeWidth: styleStrokeWidth, listening: false } as const;
        const fillProps = { fill: themeFillColor, fillEnabled: true };
        switch (activityType) {
          case 'transaction':
            return <><Rect {...styleProps} {...fillProps} x={effectiveW * 0.012} y={effectiveH * 0.012} width={effectiveW * 0.976} height={effectiveH * 0.976} cornerRadius={radius} /><Rect {...styleProps} x={effectiveW * 0.035} y={effectiveH * 0.045} width={effectiveW * 0.93} height={effectiveH * 0.91} cornerRadius={radius} fillEnabled={false} /></>;
          case 'event-sub-process':
            return <Rect {...styleProps} {...fillProps} x={effectiveW * 0.012} y={effectiveH * 0.012} width={effectiveW * 0.976} height={effectiveH * 0.976} cornerRadius={radius} dash={[4, 3]} />;
          case 'call-activity':
            return <Rect {...styleProps} {...fillProps} x={effectiveW * 0.012} y={effectiveH * 0.012} width={effectiveW * 0.976} height={effectiveH * 0.976} cornerRadius={radius} strokeWidth={Math.max(styleStrokeWidth + 0.7, 2.2)} />;
          case 'task':
          default:
            return <Rect {...styleProps} {...fillProps} x={effectiveW * 0.012} y={effectiveH * 0.012} width={effectiveW * 0.976} height={effectiveH * 0.976} cornerRadius={radius} />;
        }
      };

      const renderOneMarker = (mt: string, centerX: number, mSize: number) => {
        const markerIconSw = markerStrokeWidth * 0.78;
        switch (mt) {
          case 'sub-process':
            return <><Rect {...baseProps} strokeWidth={markerIconSw} x={centerX - mSize} y={bottomY - mSize} width={mSize * 2} height={mSize * 2} fillEnabled={false} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.5, bottomY, centerX + mSize * 0.5, bottomY]} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX, bottomY - mSize * 0.5, centerX, bottomY + mSize * 0.5]} /></>;
          case 'loop':
          case 'reset': {
            const iconBox = mSize * 1.65;
            return <Group x={centerX - iconBox / 2} y={bottomY - iconBox / 2} scaleX={iconBox / 24} scaleY={iconBox / 24} listening={false}><Path {...baseProps} strokeWidth={markerIconSw} data="M3 2v6h6" fillEnabled={false} strokeScaleEnabled={false} lineCap="round" lineJoin="round" /><Path {...baseProps} strokeWidth={markerIconSw} data="M3.51 15a9 9 0 1 0 .49-9.36L3 8" fillEnabled={false} strokeScaleEnabled={false} lineCap="round" lineJoin="round" /></Group>;
          }
          case 'multi-instance-parallel':
            return <><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.55, bottomY - mSize * 0.8, centerX - mSize * 0.55, bottomY + mSize * 0.8]} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX, bottomY - mSize * 0.8, centerX, bottomY + mSize * 0.8]} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX + mSize * 0.55, bottomY - mSize * 0.8, centerX + mSize * 0.55, bottomY + mSize * 0.8]} /></>;
          case 'multi-instance-sequential':
            return <><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.8, bottomY - mSize * 0.5, centerX + mSize * 0.8, bottomY - mSize * 0.5]} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.8, bottomY, centerX + mSize * 0.8, bottomY]} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.8, bottomY + mSize * 0.5, centerX + mSize * 0.8, bottomY + mSize * 0.5]} /></>;
          case 'ad-hoc':
            return <Path {...baseProps} strokeWidth={markerIconSw} data={`M ${centerX - mSize * 0.85} ${bottomY + mSize * 0.2} C ${centerX - mSize * 0.45} ${bottomY - mSize * 0.65} ${centerX + mSize * 0.1} ${bottomY + mSize * 0.65} ${centerX + mSize * 0.8} ${bottomY - mSize * 0.1}`} fillEnabled={false} />;
          case 'compensation':
            return <><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX - mSize * 0.08, bottomY - mSize * 0.62, centerX - mSize * 0.72, bottomY, centerX - mSize * 0.08, bottomY + mSize * 0.62]} closed fillEnabled={false} /><Line {...baseProps} strokeWidth={markerIconSw} points={[centerX + mSize * 0.66, bottomY - mSize * 0.62, centerX + mSize * 0.02, bottomY, centerX + mSize * 0.66, bottomY + mSize * 0.62]} closed fillEnabled={false} /></>;
          default:
            return null;
        }
      };

      const renderBottomMarker = () => {
        const cx = effectiveW / 2;
        if (markerTypes.length === 0) return null;
        if (markerTypes.length === 1) return renderOneMarker(markerTypes[0], cx, markerVisualSize);
        const gap = markerVisualSize * 0.6;
        const totalW = markerTypes.length * markerVisualSize * 2 + (markerTypes.length - 1) * gap;
        const startX = cx - totalW / 2 + markerVisualSize;
        return <>{markerTypes.map((mt, i) => <Group key={i} listening={false}>{renderOneMarker(mt, startX + i * (markerVisualSize * 2 + gap), markerVisualSize)}</Group>)}</>;
      };

      const renderTopLeftTask = () => {
        const taskIconScale = 1.55;
        const taskIconSize = taskSize * taskIconScale;
        const taskIconOffset = (taskIconSize - taskSize) / 2;
        const taskIconX = topLeftX - taskIconOffset;
        const taskIconY = topLeftY - taskIconOffset;
        const taskIconSw = markerStrokeWidth * 0.72;
        const boostedTaskIconSize = taskIconSize * 1.18;
        const boostedTaskOffset = (boostedTaskIconSize - taskIconSize) / 2;
        const boostedTaskX = taskIconX - boostedTaskOffset;
        const boostedTaskY = taskIconY - boostedTaskOffset;
        switch (taskType) {
          case 'send':
            return <Group x={boostedTaskX} y={boostedTaskY} scaleX={boostedTaskIconSize / 24} scaleY={boostedTaskIconSize / 24} listening={false}><Rect {...baseProps} x={5} y={7} width={14} height={10} fill={themeStrokeColor} fillEnabled strokeWidth={taskIconSw} /><Path {...baseProps} data="M5 7L12 12.5L19 7" stroke={themeFillColor} fillEnabled={false} strokeWidth={taskIconSw} lineCap="round" lineJoin="round" /></Group>;
          case 'receive':
            return <Group x={boostedTaskX} y={boostedTaskY} scaleX={boostedTaskIconSize / 24} scaleY={boostedTaskIconSize / 24} listening={false}><Rect {...baseProps} x={5} y={7} width={14} height={10} fillEnabled={false} strokeWidth={taskIconSw} /><Path {...baseProps} data="M5 7L12 12.5L19 7" fillEnabled={false} strokeWidth={taskIconSw} lineCap="round" lineJoin="round" /></Group>;
          case 'user':
            return <Group x={taskIconX} y={taskIconY} scaleX={taskIconSize / 24} scaleY={taskIconSize / 24} listening={false}><KonvaCircle {...baseProps} x={12} y={8} radius={5} fillEnabled={false} strokeWidth={taskIconSw} /><Path {...baseProps} data="M20 21a8 8 0 0 0-16 0" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw} /></Group>;
          case 'manual':
            return <Group x={taskIconX + taskIconSize / 2} y={taskIconY + taskIconSize / 2} scaleX={taskIconSize / 24} scaleY={taskIconSize / 24} rotation={90} offsetX={12} offsetY={12} listening={false}><Path {...baseProps} data="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.85} /><Path {...baseProps} data="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.85} /><Path {...baseProps} data="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.85} /><Path {...baseProps} data="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.85} /></Group>;
          case 'business-rule':
            return <Group x={boostedTaskX} y={boostedTaskY} scaleX={boostedTaskIconSize / 24} scaleY={boostedTaskIconSize / 24} listening={false}><Rect {...baseProps} x={5.2} y={6.8} width={13.6} height={10.4} fillEnabled={false} strokeWidth={taskIconSw} /><Path {...baseProps} data="M5.2 10.2H18.8M9.5 10.2V17.2" fillEnabled={false} strokeWidth={taskIconSw} lineCap="round" lineJoin="round" /></Group>;
          case 'service':
            return <Group x={boostedTaskX} y={boostedTaskY} scaleX={boostedTaskIconSize / 24 * 1.4} scaleY={boostedTaskIconSize / 24 * 1.4} listening={false}><KonvaCircle {...baseProps} x={10.2} y={13.1} radius={2.6} fillEnabled={false} strokeWidth={taskIconSw * 0.8} /><Path {...baseProps} data="M10.2 9V10.1M10.2 16.1V17.2M6.1 13.1H7.2M13.2 13.1H14.3M7.3 10.3L8.1 11.1M12.3 15.3L13.1 16.1M7.3 15.9L8.1 15.1M12.3 10.9L13.1 10.1" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.8} /><KonvaCircle {...baseProps} x={14.2} y={9.2} radius={2} fillEnabled={false} strokeWidth={taskIconSw * 0.75} /><Path {...baseProps} data="M14.2 6.3V7.1M14.2 11.3V12.1M11.3 9.2H12.1M16.3 9.2H17.1M12.3 7.3L12.9 7.9M15.5 10.5L16.1 11.1M12.3 11.1L12.9 10.5M15.5 7.9L16.1 7.3" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw * 0.75} /></Group>;
          case 'script':
            return <Group x={taskIconX - (taskIconSize * 0.88 - taskIconSize) / 2} y={taskIconY - (taskIconSize * 0.88 - taskIconSize) / 2} scaleX={taskIconSize * 0.88 / 24} scaleY={taskIconSize * 0.88 / 24} listening={false}><Path {...baseProps} data="M15 12h-5" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw} /><Path {...baseProps} data="M15 8h-5" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw} /><Path {...baseProps} data="M19 17V5a2 2 0 0 0-2-2H4" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw} /><Path {...baseProps} data="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" fillEnabled={false} lineCap="round" lineJoin="round" strokeWidth={taskIconSw} /></Group>;
          case 'none':
          default:
            return null;
        }
      };

      return <>{renderActivityStyle()}{renderTopLeftTask()}{renderBottomMarker()}</>;
    };

    const aiDef = getBpmnAiDef(variant);
    if (!aiDef) return null;

    const zoomScaledStrokeWidth = strokeWidth * stageScale;
    const fillPaths = (aiDef.fillPaths && aiDef.fillPaths.length > 0) ? aiDef.fillPaths : null;
    const activityUsesRectFill = variant === 'bpmn-activity';
    const effectiveSize = isBpmnEvent ? Math.min(effectiveW, effectiveH) : null;
    const aiScaleX = aiDef.width > 0 ? (effectiveSize ?? effectiveW) / aiDef.width : 1;
    const aiScaleY = aiDef.height > 0 ? (effectiveSize ?? effectiveH) / aiDef.height : 1;
    const pathCenterNative = 24.8;
    const pathCenterOffset = isBpmnEvent && effectiveSize ? (effectiveSize / 2 - pathCenterNative * (effectiveSize / aiDef.width)) : 0;
    const aiOffsetX = variant === 'bpmn-data-store' ? -12.5 * aiScaleX : (isBpmnEvent && effectiveSize ? (effectiveW - effectiveSize) / 2 + pathCenterOffset : 0);
    const aiOffsetY = variant === 'bpmn-data-store' ? -6.6667 * aiScaleY : (isBpmnEvent && effectiveSize ? (effectiveH - effectiveSize) / 2 + pathCenterOffset : 0);

    if (aiDef.paths.length <= 1) {
      return (
        <>
          <Group ref={aiShapeGroupRef as any} x={aiOffsetX} y={aiOffsetY} scaleX={aiScaleX} scaleY={aiScaleY}>
            {fillPaths?.map((d, idx) => <Path key={`bpmn-fill-${idx}`} {...shared} data={d} strokeEnabled={false} fillRule="evenodd" />)}
            <Path ref={shapeRef as any} {...shared} data={aiDef.paths[0] ?? ''} strokeScaleEnabled={false} fillRule="evenodd" fill={fillPaths || activityUsesRectFill ? 'transparent' : (shared as any).fill} strokeWidth={zoomScaledStrokeWidth} />
          </Group>
          {renderBpmnEventMarker()}
          {renderBpmnGatewayMarker()}
          {renderBpmnActivityMarkers()}
        </>
      );
    }
    return (
      <>
        <Group ref={aiShapeGroupRef as any} x={aiOffsetX} y={aiOffsetY} scaleX={aiScaleX} scaleY={aiScaleY}>
          {fillPaths?.map((d, idx) => <Path key={`bpmn-fill-${idx}`} {...shared} data={d} strokeEnabled={false} fillRule="evenodd" />)}
          {aiDef.paths.map((d, idx) => <Path key={`bpmn-path-${idx}`} ref={idx === 0 ? (shapeRef as any) : undefined} {...shared} data={d} strokeScaleEnabled={false} fillRule="evenodd" fill={fillPaths || activityUsesRectFill ? 'transparent' : (shared as any).fill} strokeWidth={zoomScaledStrokeWidth} />)}
        </Group>
        {renderBpmnEventMarker()}
        {renderBpmnGatewayMarker()}
        {renderBpmnActivityMarkers()}
      </>
    );
  };

  const handleGroupDoubleClick = (e: any) => {
    e.cancelBubble = true;
    if (onStartTextEdit) {
      if (textRef.current) { onStartTextEdit(textRef.current); return; }
      const group = groupRef.current;
      if (group) {
        const textNodes = (group as any).find?.('Text') || [];
        if (textNodes.length > 0) { onStartTextEdit(textNodes[0]); return; }
      }
      if (bpmnTextRef.current) { onStartTextEdit(bpmnTextRef.current); return; }
      console.warn('BpmnShapeNode: Could not find text node for editing');
    }
  };

  return (
    <>
      <Group
        ref={(nodeEl) => { groupRef.current = nodeEl as Konva.Group | null; if (typeof ref === 'function') ref(nodeEl as any); else if (ref && typeof ref === 'object') (ref as any).current = nodeEl; }}
        id={node.id}
        name="shape"
        x={node.x}
        y={node.y}
        width={effectiveW}
        height={effectiveH}
        draggable={canWrite}
        onDragStart={fireDrag(onDragStart)}
        onDragMove={fireDrag(onDragMove)}
        onDragEnd={fireDragSnapped(onDragEnd)}
        onClick={onClick}
        onDblClick={canWrite ? handleGroupDoubleClick : undefined}
        onDblTap={canWrite ? handleGroupDoubleClick : undefined}
        onTransformStart={onTransformStart}
        onTransformEnd={handleTransformEnd}
        onTransform={handleTransform}
      >
        <Group name="rotation-group" x={effectiveW / 2} y={effectiveH / 2} offsetX={effectiveW / 2} offsetY={effectiveH / 2} onTransformStart={onTransformStart} onTransformEnd={handleTransformEnd}>
          <Group name="shape-content" width={effectiveW} height={effectiveH} onTransform={onTransform}>
            {renderVariant()}
          </Group>
        </Group>
        {isBpmnAnnotation && (
          <>
            {(() => {
              const padding = 12;
              const gap = 8;
              const armLen = Math.max(10, Math.min(18, effectiveW * 0.18));
              const x = Math.min(effectiveW, armLen + gap);
              const y = padding;
              const w = Math.max(0, effectiveW - x - padding);
              const h = Math.max(0, effectiveH - padding * 2);
              const align = (attrs.textAlign as TextAlign) || 'left';
              return (
                <>
                  <Rect name="text-bounds" x={x} y={y} width={w} height={h} listening={false} fill="rgba(0,0,0,0)" strokeEnabled={false} perfectDrawEnabled={false} disableOverlayLiveResize={true} />
                  <Text ref={textRef as any} x={x} y={y} width={w} height={h} text={textValue || ''} align={align} verticalAlign="middle" listening={canWrite && !editing} visible={!editing} fontSize={fontSize} fontFamily={fontFamily} fontStyle={fontStyle} textDecoration={textDecoration} fill={hasText ? textColor : 'transparent'} wrap="char" onClick={onClick} onDblClick={(e: any) => { e.cancelBubble = true; if (onStartTextEdit && textRef.current) onStartTextEdit(textRef.current); }} onDblTap={(e: any) => { e.cancelBubble = true; if (onStartTextEdit && textRef.current) onStartTextEdit(textRef.current); }} />
                </>
              );
            })()}
          </>
        )}
      </Group>
      {!isBpmnAnnotation && (
        <Group ref={bpmnLabelGroupRef as any} x={node.x} y={node.y} listening={canWrite && !editing}>
          {(() => {
            const isDataStore = variant === 'bpmn-data-store';
            const shapeBottom = isDataStore ? (66.67 * effectiveH) / 80 : effectiveH;
            const labelY = shapeBottom + 4;
            const labelX = 0;
            const labelW = isDataStore ? Math.max(0, (75 * effectiveW) / 100) : Math.max(0, effectiveW);
            const lineHeight = fontSize * 1.2;
            const labelH = Math.max(lineHeight * 3 + 8, 48);
            const bgPad = 2;
            const textLines = textValue ? Math.ceil(textValue.length * fontSize * 0.6 / Math.max(labelW, 1)) : 1;
            const bgH = Math.max(lineHeight, Math.min(textLines, 3) * lineHeight) + bgPad * 2;
            return (
              <>
                <Rect name="text-bg" x={labelX - bgPad} y={labelY - bgPad} width={labelW + bgPad * 2} height={bgH} listening={false} fill="rgba(248,249,250,0.9)" cornerRadius={2} strokeEnabled={false} perfectDrawEnabled={false} />
                <Rect name="text-bounds" x={labelX} y={labelY} width={labelW} height={labelH} listening={false} fill="rgba(0,0,0,0)" strokeEnabled={false} perfectDrawEnabled={false} disableOverlayLiveResize={true} />
                <Text ref={bpmnTextRef as any} x={labelX} y={labelY} width={labelW} height={labelH} text={textValue || ''} wrap="word" align={isDataStore ? 'center' : hAlign} verticalAlign="top" listening={canWrite && !editing} visible={!editing} fontSize={fontSize} fontFamily={fontFamily} fontStyle={fontStyle} textDecoration={textDecoration} fill={hasText ? themeTextColor : 'transparent'} onClick={onClick} onDblClick={(e: any) => { e.cancelBubble = true; if (onStartTextEdit && bpmnTextRef.current) onStartTextEdit(bpmnTextRef.current); }} onDblTap={(e: any) => { e.cancelBubble = true; if (onStartTextEdit && bpmnTextRef.current) onStartTextEdit(bpmnTextRef.current); }} />
              </>
            );
          })()}
        </Group>
      )}
    </>
  );
});
BpmnShapeNodeBase.displayName = 'BpmnShapeNode';

export const BpmnShapeNode = BpmnShapeNodeBase;
export default BpmnShapeNode;
